import type { ShippingCountry, ShippingRuleSettings } from "@cleanbudget/contract/shipping-rule";
import { Card, CardContent, CardHeader, CardTitle } from "@cleanbudget/ui/components/card";
import { IconCheck } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { BackLink } from "@/components/back-link";
import { Form, FormError, SubmitButton, SwitchField, TextField, useZodForm } from "@/components/form/form";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { centsToInput, parseMoney } from "@/lib/money";
import { orpc } from "@/lib/orpc";

const settingsQuery = () => orpc.shippingRule.list.queryOptions();

/** Instellingen → Verzendregels: a rate and a free-from threshold per country and customer group. */
export const Route = createFileRoute("/_admin/settings/shipping-rule/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(settingsQuery()),
	component: ShippingRulesPage,
});

/**
 * The form: one row per country and group, in the page's order. Amounts are typed in euros; a row
 * that does not ship needs none. Its output is the whole set of rules, as `replace` takes it.
 */
const Rows = z
	.object({
		rows: z.array(
			z.object({
				country: z.string(),
				customerGroupId: z.string(),
				ships: z.boolean(),
				rate: z.string(),
				freeFrom: z.string(),
			}),
		),
	})
	.transform(({ rows }, ctx) => {
		const rules = [];
		for (const [index, row] of rows.entries()) {
			if (!row.ships) continue;
			const rate = parseMoney(row.rate);
			const freeFrom = row.freeFrom.trim() === "" ? null : parseMoney(row.freeFrom);
			const issue = (field: string) =>
				ctx.addIssue({ code: "custom", path: ["rows", index, field], params: { i18n: "money" }, input: row });
			if (rate === undefined) issue("rate");
			if (freeFrom === undefined) issue("freeFrom");
			if (rate === undefined || freeFrom === undefined) continue;
			rules.push({
				country: row.country as ShippingCountry,
				customerGroupId: row.customerGroupId,
				rate,
				freeFrom,
			});
		}
		return { rules };
	});
type RowsInput = z.input<typeof Rows>;

const toValues = (settings: ShippingRuleSettings): RowsInput => ({
	rows: settings.countries.flatMap((country) =>
		settings.customerGroups.map((group) => {
			const rule = settings.rules.find((r) => r.country === country && r.customerGroupId === group.id);
			return {
				country,
				customerGroupId: group.id,
				ships: rule !== undefined,
				rate: rule ? centsToInput(rule.rate) : "",
				freeFrom: rule?.freeFrom == null ? "" : centsToInput(rule.freeFrom),
			};
		}),
	),
});

function ShippingRulesPage() {
	const { t } = useTranslation("shippingRule");
	const { t: tSettings } = useTranslation("settings");
	const { data } = useSuspenseQuery(settingsQuery());
	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/settings">{tSettings("title")}</BackLink>}
				title={t("title")}
				description={t("intro")}
			/>
			{/* Another set of countries or groups starts the form again; a save resets it itself. */}
			<RulesForm
				key={[...data.countries, ...data.customerGroups.map((group) => group.id)].join(" ")}
				settings={data}
			/>
		</div>
	);
}

function RulesForm({ settings }: { settings: ShippingRuleSettings }) {
	const { t } = useTranslation("shippingRule");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const form = useZodForm(Rows, { defaultValues: toValues(settings) });
	const { isDirty } = useFormState({ control: form.control });
	const [saved, setSaved] = useState(false);
	const replace = useMutation(orpc.shippingRule.replace.mutationOptions());

	const submit = async (body: z.output<typeof Rows>) => {
		try {
			const answer = await replace.mutateAsync(body);
			form.reset(toValues(answer));
			setSaved(true);
			queryClient.setQueryData(settingsQuery().queryKey, answer);
		} catch (error) {
			form.setError("root", {
				type: "server",
				message:
					apiErrorTag(error) === "ShippingRuleCustomerGroupNotFound"
						? t("groupGone")
						: tCommon("form.failed"),
			});
		}
	};

	let index = 0;
	return (
		<Form form={form} onSubmit={submit}>
			{settings.countries.map((country) => (
				<Card key={country}>
					<CardHeader>
						<CardTitle>
							<h2>{t(`country.${country}`)}</h2>
						</CardTitle>
					</CardHeader>
					<CardContent>
						<ul className="flex flex-col divide-y">
							{settings.customerGroups.map((group) => {
								const rowIndex = index++;
								return (
									<li key={group.id} className="py-4 first:pt-0 last:pb-0">
										<RuleRow
											control={form.control}
											index={rowIndex}
											name={group.name}
											basis={t(`basis.${group.vatBasis}`)}
										/>
									</li>
								);
							})}
						</ul>
					</CardContent>
				</Card>
			))}
			<FormError control={form.control} />
			<div className="flex flex-wrap items-center gap-3">
				<SubmitButton control={form.control}>{t("submit")}</SubmitButton>
				{saved && !isDirty ? (
					<output className="flex items-center gap-1 text-sm text-muted-foreground">
						<IconCheck className="size-4" aria-hidden />
						{tCommon("form.saved")}
					</output>
				) : null}
			</div>
		</Form>
	);
}

type RowsControl = ReturnType<typeof useZodForm<typeof Rows>>["control"];

function RuleRow({
	control,
	index,
	name,
	basis,
}: {
	control: RowsControl;
	index: number;
	name: string;
	basis: string;
}) {
	const { t } = useTranslation("shippingRule");
	const ships = useWatch({ control, name: `rows.${index}.ships` });
	return (
		<div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] sm:items-start">
			<div className="flex flex-col gap-2">
				<div className="flex flex-col">
					<span className="font-medium">{name}</span>
					<span className="text-sm text-muted-foreground">{basis}</span>
				</div>
				<SwitchField control={control} name={`rows.${index}.ships`} label={t("field.ships")} />
			</div>
			{ships ? (
				<div className="grid grid-cols-2 gap-3">
					<TextField
						control={control}
						name={`rows.${index}.rate`}
						label={t("field.rate")}
						inputMode="decimal"
						autoComplete="off"
					/>
					<TextField
						control={control}
						name={`rows.${index}.freeFrom`}
						label={t("field.freeFrom")}
						description={t("field.freeFromDescription")}
						inputMode="decimal"
						autoComplete="off"
					/>
				</div>
			) : (
				<p className="text-sm text-muted-foreground sm:pt-1">{t("notShipped")}</p>
			)}
		</div>
	);
}
